import { useEffect, type Dispatch, type MutableRefObject, type SetStateAction } from "react";
import { api, ApiError } from "../../lib/api";

type Conversation = Awaited<ReturnType<typeof api.getThread>>;
/** Ordinary chats retain their readable view on transient background failures.
 * Exact-source mode clears protected content on definitive source admission loss. */
export function useConversationPolling(input: { userId: number | undefined; threadId: string | null; exactSource: boolean;
  requestRef: MutableRefObject<number>; readRef: MutableRefObject<number>;
  setConversation: Dispatch<SetStateAction<Conversation | null>>; setError: Dispatch<SetStateAction<string | null>> }) {
  const { userId, threadId, exactSource, requestRef, readRef, setConversation, setError } = input;
  useEffect(() => {
    if (!userId || !threadId) return;
    const request = requestRef.current;
    let active = true;
    const refresh = () => {
      if (document.visibilityState !== "visible") return;
      const read = ++readRef.current;
      const current = () => active && request === requestRef.current && read === readRef.current;
      void api.getThread(threadId).then(conversation => {
        if (!current()) return;
        setConversation(conversation);
        if (!conversation && exactSource) setError("This exact proposal is unavailable in the selected account or profile.");
      }).catch(failure => {
        if (current() && exactSource && failure instanceof ApiError && [401, 403, 404].includes(failure.status)) {
          setConversation(null); setError("This exact proposal is unavailable in the selected account or profile.");
        }
      });
    };
    const timer = setInterval(refresh, 15000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => { active = false; clearInterval(timer); window.removeEventListener("focus", refresh); document.removeEventListener("visibilitychange", refresh); };
  }, [userId, threadId, exactSource, requestRef, readRef, setConversation, setError]);
}
